import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { z } from "zod";

import { AuthPageLayout } from "@/components/auth/auth-page-layout";
import { CredentialsForm } from "@/components/auth/login/credentials-form";
import { SocialLoginSection } from "@/components/auth/login/social-login-section";
import { PasswordResetRequiredDrawer } from "@/components/ui/drawer/password-reset-required-drawer";
import { UnexpectedErrorDrawer } from "@/components/ui/drawer/unexpected-error-drawer";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { PageDescription, PageTitle } from "@/components/ui/layout/page/page";
import {
  captureAuthError,
  trackLoginFailed,
  trackLoginStarted,
  trackLoginSucceeded,
} from "@/lib/auth/auth-tracking";
import { useLastUsedProvider } from "@/hooks/use-last-used-provider";
import { useReturnToNavigation } from "@/hooks/use-return-to-navigation";
import { useSignin } from "@/hooks/use-signin";
import { useStytchErrorDrawer } from "@/hooks/use-stytch-error-drawer";
import { isResetPasswordError } from "@/lib/mappers/stytch-error";
import { redirectToReturnToOrFallback } from "@/lib/utils/redirect-to-return-to";

import type { JSX } from "react";

const CredentialsLoginPage = (): JSX.Element => {
  const navigate = useNavigate();
  const router = useRouter();
  const searchParams = useSearch({ from: "/auth/signin" });
  const { handleStytchError } = useStytchErrorDrawer();
  const { signInWithPassword, signInWithGoogle, signInWithApple } = useSignin();
  const { lastUsedProvider, saveLastUsedProvider } = useLastUsedProvider();
  const { navigateToReturnToOrFallback } = useReturnToNavigation();

  const handleLogin = async (email: string, password: string): Promise<void> => {
    trackLoginStarted("senha");

    try {
      await signInWithPassword(email, password);

      trackLoginSucceeded("senha");
      saveLastUsedProvider("email");
      navigateToReturnToOrFallback("/");
    } catch (error) {
      trackLoginFailed("senha", error);
      captureAuthError(error, { flow: "login", method: "senha" });

      if (isResetPasswordError(error)) {
        showPasswordResetRequiredDrawer(email);
        return;
      }

      handleStytchError(error, {
        fallback: "Não foi possível realizar o login. Tente novamente.",
      });
    }
  };

  const handleGoogleLogin = (): void => {
    trackLoginStarted("google");
    saveLastUsedProvider("google");
    void signInWithGoogle();
  };

  const handleAppleLogin = (): void => {
    trackLoginStarted("apple");
    saveLastUsedProvider("apple");
    void signInWithApple();
  };

  const showUnexpectedErrorDrawer = (): void => {
    void navigate({
      to: "/auth/signin",
      search: { action: "error" },
    });
  };

  const hideUnexpectedErrorDrawer = (): void => {
    if (searchParams.action === "error") {
      router.history.back();
    }
  };

  const showPasswordResetRequiredDrawer = (email: string): void => {
    void navigate({
      to: "/auth/signin",
      search: { action: "reset_password", email },
    });
  };

  const hidePasswordResetRequiredDrawer = (): void => {
    if (searchParams.action === "reset_password") {
      router.history.back();
    }
  };

  return (
    <AuthPageLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
      </FlowHeader>

      <div className="flex flex-col flex-1 p-6 gap-10 sm:flex-none sm:gap-6 sm:pb-10">
        <div className="flex flex-col gap-2">
          <PageTitle className="pt-0">Acesse sua conta</PageTitle>
          <PageDescription>
            Use o e-mail informado na contratação e a senha criada no primeiro acesso
          </PageDescription>
        </div>

        <CredentialsForm
          onSubmit={handleLogin}
          isLastUsed={lastUsedProvider === "email"}
        />

        <SocialLoginSection
          onGoogleLogin={handleGoogleLogin}
          onAppleLogin={handleAppleLogin}
          lastUsedProvider={lastUsedProvider ?? undefined}
        />
      </div>

      <UnexpectedErrorDrawer
        open={searchParams.action === "error"}
        setOpen={(isOpen) => {
          if (isOpen) {
            showUnexpectedErrorDrawer();
          } else {
            hideUnexpectedErrorDrawer();
          }
        }}
      />

      <PasswordResetRequiredDrawer
        open={searchParams.action === "reset_password"}
        setOpen={(isOpen) => {
          if (!isOpen) {
            hidePasswordResetRequiredDrawer();
          }
        }}
        email={searchParams.email ?? ""}
      />
    </AuthPageLayout>
  );
};

export const Route = createFileRoute("/auth/signin")({
  validateSearch: z.object({
    action: z.string().optional(),
    email: z.string().optional(),
  }),
  beforeLoad: async ({ context }) => {
    const isAuthenticated = await context.stytchAuthenticationClient.isAuthenticated();

    if (isAuthenticated) {
      redirectToReturnToOrFallback("/");
    }
  },
  component: CredentialsLoginPage,
});
